import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import { normalizeCasinoRankingRows } from "./normalizeCasinoRankingRows";
import { buildCasinoKpiMoneyTypeParam } from "./casinoKpiFilters";

export type CasinoByStateV2Filters = {
  start_date: string;
  end_date: string;
  money_type?: string[];
};

export type CasinoStateV2Row = {
  name: string;
  abbr: string;
  turnover: number;
  ggr: number;
  total_rounds: number;
};

// Two-letter abbreviation lookup. Used as a fallback when the BE returns
// state-keyed rows without an explicit `abbr` field. Lives next to the hook
// so the parser layer stays state-shape-agnostic and the component layer
// can treat the row's `abbr` as authoritative.
const STATE_ABBR_BY_NAME: Record<string, string> = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
  Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD",
  Massachusetts: "MA", Michigan: "MI", Minnesota: "MN", Mississippi: "MS",
  Missouri: "MO", Montana: "MT", Nebraska: "NE", Nevada: "NV",
  "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM",
  "New York": "NY", "North Carolina": "NC", "North Dakota": "ND",
  Ohio: "OH", Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA",
  "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD",
  Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT", Virginia: "VA",
  Washington: "WA", "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY",
  "District of Columbia": "DC",
};

const resolveAbbr = (name: string, rawAbbr: string): string => {
  const trimmedRaw = rawAbbr.trim().toUpperCase();
  if (trimmedRaw.length === 2) return trimmedRaw;
  return STATE_ABBR_BY_NAME[name.trim()] ?? "";
};

const fetchCasinoByStateV2 = async (
  filters: CasinoByStateV2Filters,
): Promise<CasinoStateV2Row[]> => {
  const instance = getPrivateInstance();
  const { money_type, ...dateFilters } = filters;
  const response = await instance.get(API_ROUTES.CASINO_BY_STATE_V2, {
    params: {
      ...dateFilters,
      ...buildCasinoKpiMoneyTypeParam(money_type),
    },
  });
  return normalizeCasinoRankingRows(response?.data?.data)
    .map((row) => ({
      name: row.name,
      abbr: resolveAbbr(row.name, row.abbr),
      turnover: row.turnover.value,
      ggr: row.ggr.value,
      total_rounds: row.total_rounds.value,
    }))
    // Drop rows we can't place on the map (no recognised state -> no abbr).
    .filter((row) => row.abbr.length === 2);
};

export const useGetCasinoByStateV2 = (filters: CasinoByStateV2Filters) => {
  const { start_date, end_date, money_type } = filters;

  return useQuery<CasinoStateV2Row[], Error>({
    queryKey: [
      "CASINO_BY_STATE_V2",
      start_date,
      end_date,
      money_type?.join(",") ?? "",
    ],
    queryFn: () => fetchCasinoByStateV2(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
